import ButtonBack from "@/view/components/ButtonBack";
import useAvailableOffersClt, {
  CltSimulationMode,
} from "./useAvailableOffersClt";
import { Spinner } from "@/view/components/ui/spinner";
import { Dialog } from "@/view/components/ui/dialog";
import MakeProposalCard from "../WithdrawSimulation/components/MakeProposalCard";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";
import { cn } from "@/app/utils/cn";
import NewWithdrawModal from "../WithdrawSimulation/components/NewWithdrawModal";
import { OfferCard } from "./components/OfferCard";
import { ITableClt } from "@/app/services/cltService/getTables";
import { Input } from "@/view/components/ui/input";
import { Slider } from "@/view/components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "@/view/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/view/components/ui/select";
import { EmptyMessage } from "@/view/components/EmptyMessage";
import Tab from "../WithdrawSimulation/components/Tab";
import Loading from "../WithdrawSimulation/components/Loading";
import TableTransactions from "../WithdrawSimulation/components/TableTransactions";
import CustomerDetails from "../WithdrawSimulation/components/CustomerDetails";
import { Moneys, Personalcard, Refresh } from "iconsax-react";
import { Table } from "lucide-react";
import { useEffect, useRef } from "react";
import { FormProvider } from "react-hook-form";

const TAB_TRIGGER_CLASS =
  "h-full rounded-md font-medium text-black04 transition-colors hover:text-black01 data-[state=active]:border-0 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm";

export default function AvailableOffersClt() {
  const {
    list,
    isLoading,
    error,
    selectedTable,
    selectedIndex,
    setSelectedIndex,
    prazos,
    selectedPrazo,
    handleSelectPrazo,
    visibleOffers,
    matricula,
    cnpjEmpresa,
    currentValue,
    setCurrentValue,
    valueLabel,
    hasSlider,
    sliderMin,
    sliderMax,
    sliderValue,
    sliderStep,
    maxInstallment,
    exceedsMaxInstallment,
    simulationMode,
    handleChangeSimulationMode,
    isLoadingAvailableOffers,
    isErrorAvailableOffers,
    isErrorFinancedValue,
    handleRefresh,
    message,
    tabs,
    setSelectedTab,
    transactions,
    setSkip,
    form,
    customer,
    customerIsLoading,
  } = useAvailableOffersClt();

  const offerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const offersListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedIndex === null) return;

    const container = offersListRef.current;
    const card = offerRefs.current[selectedIndex];

    if (!container || !card) return;

    const cardStart = card.offsetLeft - container.offsetLeft;
    const cardEnd = cardStart + card.offsetWidth;
    const viewStart = container.scrollLeft;
    const viewEnd = viewStart + container.clientWidth;

    if (cardStart >= viewStart && cardEnd <= viewEnd) return;

    container.scrollTo({
      left: cardStart < viewStart ? cardStart : cardEnd - container.clientWidth,
      behavior: "smooth",
    });
  }, [selectedIndex]);

  if (isLoadingAvailableOffers) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-8 xl:flex-row">
        <Spinner />
      </div>
    );
  }

  if (isErrorAvailableOffers) {
    const errorMessage =
      (error as any)?.response?.data?.message ||
      (error as any)?.message ||
      "Ocorreu um erro ao buscar as ofertas.";

    return (
      <div>
        <ButtonBack label="Ofertas" />
        <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
          <span className="text-base font-semibold text-black01">
            {errorMessage}
          </span>
        </div>
      </div>
    );
  }

  if (!isLoading && !selectedTable && list.length === 0) {
    return (
      <>
        <ButtonBack label="Ofertas" />
        <div className="flex h-screen flex-col items-center justify-center gap-8 xl:flex-row">
          <span className="text-base font-semibold text-black01">
            {message || "Nenhuma oferta encontrada"}
          </span>
        </div>
      </>
    );
  }

  return (
    <div>
      <ButtonBack label="Ofertas" />

      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-5 rounded-2xl border-gray01 sm:p-0 md:w-[30%] md:border md:p-5 ">
          <div className="flex flex-col gap-4 rounded-2xl p-5 md:rounded-none md:p-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-black01">
                  Grandino CLT - CONSIGNADO PRIVADO
                </p>
                <p className="mt-1 text-xs font-medium text-black04">
                  Simular por
                </p>
              </div>

              <button
                type="button"
                title="Atualizar simulação"
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-input bg-background text-black04 transition-colors hover:bg-gray01 disabled:opacity-50"
              >
                <Refresh size={18} />
              </button>
            </div>

            <Tabs
              value={simulationMode}
              onValueChange={(value) =>
                handleChangeSimulationMode(value as CltSimulationMode)
              }
            >
              <TabsList className="grid h-11 w-full grid-cols-2 gap-1 rounded-lg bg-gray01 p-1">
                <TabsTrigger className={TAB_TRIGGER_CLASS} value="liberado">
                  Valor liberado
                </TabsTrigger>
                <TabsTrigger className={TAB_TRIGGER_CLASS} value="parcela">
                  Valor da parcela
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex items-stretch gap-2">
              <div
                className={cn(
                  "flex-1 rounded-xl border-[1.5px] border-primary px-4 py-3",
                  exceedsMaxInstallment && "border-destructive",
                )}
              >
                <label className="text-xs font-medium text-black04">
                  {valueLabel}
                </label>
                <Input
                  value={addMoneySimbols(currentValue)}
                  className="h-auto border-none bg-transparent p-0 text-2xl font-extrabold text-black01 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  onChange={(e) => {
                    const onlyNumbers = e.target.value.replace(/\D/g, "");

                    setCurrentValue(Number(onlyNumbers) / 100);
                  }}
                />
              </div>

              {prazos.length > 0 && (
                <div className="min-w-[110px] rounded-xl border-[1.5px] border-gray01 px-4 py-3">
                  <label className="text-xs font-medium text-black04">
                    Prazo
                  </label>
                  <Select
                    value={selectedPrazo ? String(selectedPrazo) : undefined}
                    onValueChange={(value) => handleSelectPrazo(Number(value))}
                    disabled={isLoading}
                  >
                    <SelectTrigger className="h-auto border-none bg-transparent p-0 text-2xl font-extrabold text-black01 shadow-none focus:ring-0 focus:ring-offset-0">
                      <SelectValue placeholder="--" />
                    </SelectTrigger>
                    <SelectContent>
                      {prazos.map((prazo) => (
                        <SelectItem key={prazo} value={String(prazo)}>
                          {prazo}x
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {hasSlider && (
              <div>
                <Slider
                  min={sliderMin}
                  max={sliderMax}
                  step={sliderStep}
                  value={sliderValue}
                  onChange={(e) => setCurrentValue(Number(e.target.value))}
                />

                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-black01">
                    {addMoneySimbols(sliderMin)}
                  </span>
                  <span className="text-sm font-bold text-black01">
                    {addMoneySimbols(sliderMax)}
                  </span>
                </div>
              </div>
            )}

            {simulationMode === "parcela" && (
              <p
                className={cn(
                  "text-xs text-black04",
                  exceedsMaxInstallment && "font-medium text-destructive",
                )}
              >
                Parcela máxima permitida: {addMoneySimbols(maxInstallment)}
              </p>
            )}

            <div className="flex items-start gap-2.5 rounded-xl border-[1.5px] border-primary bg-primary/10 px-3.5 py-3 shadow-sm">
              <span className="mt-0.5 flex h-[22px] w-[22px] min-w-[22px] items-center justify-center rounded-full bg-primary text-[13px] font-extrabold text-white">
                ✓
              </span>
              <div>
                <p className="text-[15px] font-extrabold text-primary">
                  Crédito aprovado
                </p>
                <p className="mt-0.5 text-xs text-primary/85">
                  O crédito de seu cliente já foi aprovado e está pronto para
                  contratação. Preencha os dados corretamente e realize a
                  proposta para finalizar a contratação.
                </p>
              </div>
            </div>
          </div>

          <Dialog>
            <div className="[&>button]:w-full">
              <MakeProposalCard
                disabled={
                  selectedTable?.ValorLiquidoCliente === 0 ||
                  isLoading ||
                  isErrorFinancedValue ||
                  exceedsMaxInstallment
                }
              />
            </div>
            <NewWithdrawModal
              table={selectedTable}
              type="CLT"
              matricula={matricula}
              cnpjEmpresa={cnpjEmpresa}
            />
          </Dialog>
        </div>
      </div>

      <div className="mt-8">
        <div className="z-10 flex overflow-x-auto border-b">
          <Tab onSelect={() => setSelectedTab(0)} isActive={tabs.tables}>
            <Table /> Tabelas
          </Tab>

          <Tab onSelect={() => setSelectedTab(1)} isActive={tabs.transaction}>
            <Moneys /> Transações
          </Tab>

          <div className="min-w-[245px]">
            <Tab
              onSelect={() => setSelectedTab(2)}
              isActive={tabs.personalInfo}
            >
              <Personalcard /> Informações pessoais
            </Tab>
          </div>
        </div>

        {tabs.tables && (
          <div className="flex flex-col gap-4">
            {isErrorFinancedValue && (
              <div className="flex justify-center pt-20">
                <span className="text-base font-semibold text-black01">
                  {(error as any)?.response?.data?.message ||
                    (error as any)?.message ||
                    "Ocorreu um erro ao buscar as ofertas."}
                </span>
              </div>
            )}

            {isLoading && (
              <div className="flex justify-center pt-20">
                <Spinner />
              </div>
            )}

            {!isLoading && (
              <div
                ref={offersListRef}
                className="flex gap-4 overflow-x-auto p-4 max-sm:snap-x max-sm:snap-mandatory"
              >
                {visibleOffers.map(({ offer, index }) => (
                  <div
                    key={index}
                    ref={(node) => (offerRefs.current[index] = node)}
                    className="max-sm:flex-shrink-0 max-sm:snap-center"
                  >
                    <OfferCard
                      selected={selectedIndex === index}
                      onClick={() => setSelectedIndex(index)}
                      offer={offer as unknown as ITableClt}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tabs.transaction &&
          (transactions.isLoading ? (
            <Loading />
          ) : transactions.data?.transactions?.length ? (
            <TableTransactions data={transactions.data} setSkip={setSkip} />
          ) : (
            <EmptyMessage description="Aqui será exibido o histórico de transações do cliente, você poderá acompanhar o progresso e o historico deste cliete." />
          ))}

        {tabs.personalInfo &&
          (customerIsLoading ? (
            <Loading />
          ) : customer ? (
            <FormProvider {...form}>
              <CustomerDetails />
            </FormProvider>
          ) : (
            <EmptyMessage description="Aqui será exibido as informações do cliente, para que você possa validar tais informações" />
          ))}
      </div>
    </div>
  );
}
